import { useEffect, useState } from "react";

import { settingsApi, toArray } from "../Services";

/**
 * Manage Catalog -> Settings, read once.
 *
 * `emergencyApproachEnabled` comes back either as a bare boolean or as an
 * object carrying its own audit fields, `{ value, updatedBy, updatedOn }` —
 * the Manage Catalog tab already handles both. Reading it as a boolean alone
 * meant an object always failed the check and the toggle had no effect.
 *
 * `null` means "not known yet, or the call failed", which the two callers
 * treat differently: the Add Work Order wizard stays permissive so an
 * unreachable settings call never blocks a legitimate choice, while the list
 * filter only offers what Settings positively confirms.
 */
export const usePanelSettings = () => {
  const [emergencyEnabled, setEmergencyEnabled] = useState<boolean | null>(null);
  /* The ceiling a work order's SOR lines must stay under. The server enforces
     it too (validateContractLimit), so reading it here keeps the warning and
     the rejection in step instead of the UI guessing at 3 million. */
  const [contractLimit, setContractLimit] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    settingsApi
      .getAll()
      .then((rows: any) => {
        if (cancelled) return;
        const record: any = toArray(rows)[0] ?? rows;

        /* Every setting arrives as `{ value, updatedBy, updatedOn }`; a bare
           value is still accepted in case an older response sends one. */
        const read = (field: any) => (field && typeof field === "object" ? field.value : field);

        const limit = Number(read(record?.contractLimit));
        if (Number.isFinite(limit) && limit > 0) setContractLimit(limit);

        const flag = read(record?.emergencyApproachEnabled);
        if (flag === undefined || flag === null) return;
        setEmergencyEnabled(Boolean(flag));
      })
      .catch(() => undefined); // leave it unknown

    return () => {
      cancelled = true;
    };
  }, []);

  return { emergencyEnabled, contractLimit };
};
